import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  getDownloadHistory, 
  getAllDownloadHistory, 
  deleteDownloadRecord,
  downloadPdfReport,
  downloadSingleWorkOrderCsv,
  downloadPhotosZip
} from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import DownloadDialog from '../components/DownloadDialog';
import { 
  Download, 
  FileText, 
  Table2, 
  Package, 
  FolderArchive, 
  Trash2, 
  RefreshCw,
  Clock,
  User,
  Filter,
  FileDown,
  ExternalLink,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const DownloadsPage = () => {
  const { user, isAdmin, isSupervisor } = useAuth();
  const navigate = useNavigate();
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'all'
  
  // Download dialog state
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [downloadDialogConfig, setDownloadDialogConfig] = useState({
    fileType: '',
    defaultFilename: '',
    fileSize: null,
    workOrderId: null
  });

  useEffect(() => {
    fetchDownloads();
  }, [filter, viewMode]);

  const fetchDownloads = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') {
        params.file_type = filter;
      }
      
      const response = viewMode === 'all' && (isAdmin() || isSupervisor())
        ? await getAllDownloadHistory(params)
        : await getDownloadHistory(params);
      
      setDownloads(response.data);
    } catch (error) {
      console.error('Failed to fetch downloads:', error);
      toast.error('Failed to load download history');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (downloadId) => {
    try {
      await deleteDownloadRecord(downloadId);
      setDownloads(downloads.filter(d => d.id !== downloadId));
      toast.success('Download record removed');
    } catch (error) {
      toast.error('Failed to delete record');
    }
  };

  // Handle re-download - opens the download dialog
  const handleRedownload = (download) => {
    if (!download.work_order_id && download.file_type !== 'csv_export') {
      toast.error('Cannot re-download: Work order information missing');
      return;
    }
    
    setDownloadDialogConfig({
      fileType: download.file_type,
      defaultFilename: download.filename?.replace(/\.[^/.]+$/, '') || 'download',
      fileSize: download.file_size,
      workOrderId: download.work_order_id
    });
    setShowDownloadDialog(true);
  };

  // Handle the actual download when dialog confirms
  const handleDownloadFile = async (filename, returnBlob = false) => {
    const { fileType, workOrderId } = downloadDialogConfig;
    
    try {
      let response;
      
      switch (fileType) {
        case 'pdf_report':
          response = await downloadPdfReport(workOrderId);
          break;
        case 'csv_export':
          response = await downloadSingleWorkOrderCsv(workOrderId);
          break;
        case 'photos_zip':
          response = await downloadPhotosZip(workOrderId);
          break;
        case 'job_packet':
          // Job packets are JSON, need special handling
          toast.info('For Job Packets, please go to the Work Order and click "Generate Job Packet"');
          setShowDownloadDialog(false);
          if (workOrderId) {
            navigate(`/work-orders/${workOrderId}`);
          }
          return null;
        default:
          toast.error('Unknown file type');
          return null;
      }
      
      if (returnBlob) {
        return response.data;
      }
      
      // Direct download fallback
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded successfully');
      return response.data;
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download file');
      return null;
    }
  };

  // Navigate to work order
  const handleViewWorkOrder = (workOrderId) => {
    if (workOrderId) {
      navigate(`/work-orders/${workOrderId}`);
    }
  };

  const getFileTypeIcon = (fileType) => {
    switch (fileType) {
      case 'pdf_report':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'csv_export':
        return <Table2 className="w-5 h-5 text-green-500" />;
      case 'job_packet':
        return <Package className="w-5 h-5 text-purple-500" />;
      case 'photos_zip':
        return <FolderArchive className="w-5 h-5 text-blue-500" />;
      default:
        return <FileDown className="w-5 h-5 text-slate-500" />;
    }
  };

  const getFileTypeBadge = (fileType) => {
    const styles = {
      pdf_report: 'bg-red-100 text-red-700',
      csv_export: 'bg-green-100 text-green-700',
      job_packet: 'bg-purple-100 text-purple-700',
      photos_zip: 'bg-blue-100 text-blue-700'
    };
    const labels = {
      pdf_report: 'PDF Report',
      csv_export: 'CSV Export',
      job_packet: 'Job Packet',
      photos_zip: 'Photos ZIP'
    };
    return (
      <Badge className={styles[fileType] || 'bg-slate-100 text-slate-700'}>
        {labels[fileType] || fileType}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="p-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Download className="w-7 h-7 text-[#0056D2]" />
            Downloads & Exports
          </h1>
          <p className="text-slate-500 mt-1">View and manage your exported files and reports</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* View Mode Toggle (Admin/Supervisor only) */}
          {(isAdmin() || isSupervisor()) && (
            <Select value={viewMode} onValueChange={setViewMode}>
              <SelectTrigger className="w-[140px]">
                <User className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="my">My Downloads</SelectItem>
                <SelectItem value="all">All Users</SelectItem>
              </SelectContent>
            </Select>
          )}
          
          {/* Filter */}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[150px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="pdf_report">PDF Reports</SelectItem>
              <SelectItem value="csv_export">CSV Exports</SelectItem>
              <SelectItem value="job_packet">Job Packets</SelectItem>
              <SelectItem value="photos_zip">Photos ZIP</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Refresh */}
          <Button variant="outline" onClick={fetchDownloads} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-2xl font-bold text-red-700">
                  {downloads.filter(d => d.file_type === 'pdf_report').length}
                </p>
                <p className="text-xs text-red-600">PDF Reports</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Table2 className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-green-700">
                  {downloads.filter(d => d.file_type === 'csv_export').length}
                </p>
                <p className="text-xs text-green-600">CSV Exports</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold text-purple-700">
                  {downloads.filter(d => d.file_type === 'job_packet').length}
                </p>
                <p className="text-xs text-purple-600">Job Packets</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FolderArchive className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-blue-700">
                  {downloads.filter(d => d.file_type === 'photos_zip').length}
                </p>
                <p className="text-xs text-blue-600">Photos ZIP</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Downloads List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Download History</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : downloads.length === 0 ? (
            <div className="text-center py-12">
              <Download className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No downloads yet</p>
              <p className="text-sm text-slate-400 mt-1">
                Export reports, job packets, or photos to see them here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {downloads.map((download) => (
                <div 
                  key={download.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {getFileTypeIcon(download.file_type)}
                    <div>
                      <p className="font-medium text-slate-800">{download.filename}</p>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(download.created_at)}
                        </span>
                        {download.file_size && (
                          <span>{formatFileSize(download.file_size)}</span>
                        )}
                        {viewMode === 'all' && download.user_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {download.user_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {getFileTypeBadge(download.file_type)}
                    {download.work_order_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewWorkOrder(download.work_order_id)}
                        className="text-xs"
                        title="View Work Order"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        View WO
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRedownload(download)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Re-download this file"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Re-download
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(download.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      title="Remove from history"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Download Dialog */}
      <DownloadDialog
        open={showDownloadDialog}
        onOpenChange={setShowDownloadDialog}
        fileType={downloadDialogConfig.fileType}
        defaultFilename={downloadDialogConfig.defaultFilename}
        fileSize={downloadDialogConfig.fileSize}
        onDownload={handleDownloadFile}
      />
    </div>
  );
};

export default DownloadsPage;
